import { toast } from '@heidi/design-system/toast';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { WORKFLOW_KEYS } from '../../contracts/keys';

export type RoutineApprovalResolve = (
  approvalId: string,
  verdict: 'approve' | 'deny',
  reason?: string,
  modifiedInput?: Record<string, unknown> | null
) => Promise<void>;

export function useRoutineApprovalResolve(
  approvalId: string,
  onResolve: RoutineApprovalResolve,
  workflowId?: string
) {
  const intl = useIntl();
  const qc = useQueryClient();
  const [pendingVerdict, setPendingVerdict] = useState<'approve' | 'deny' | null>(null);
  const isBusy = pendingVerdict !== null;

  const submit = useCallback(
    (verdict: 'approve' | 'deny') => {
      if (isBusy) return;
      setPendingVerdict(verdict);
      void onResolve(approvalId, verdict, undefined, null)
        .then(() => {
          // Invalidate routine caches only on approve (same as the pre-move app hook).
          if (verdict !== 'approve') return;
          void qc.invalidateQueries({ queryKey: WORKFLOW_KEYS.all });
          if (workflowId) {
            void qc.invalidateQueries({
              queryKey: WORKFLOW_KEYS.schedule(workflowId),
              refetchType: 'all',
            });
            void qc.invalidateQueries({
              queryKey: WORKFLOW_KEYS.eventRules(workflowId),
              refetchType: 'all',
            });
          }
        })
        .catch((err: unknown) => {
          setPendingVerdict(null);
          toast({
            title: intl.formatMessage({
              defaultMessage: "Couldn't resolve",
              id: 'TajBHFT+ws',
            }),
            description: err instanceof Error ? err.message : 'Unknown error',
            tone: 'destructive',
          });
        });
    },
    [approvalId, intl, isBusy, onResolve, qc, workflowId]
  );

  return { submit, pendingVerdict, isBusy };
}
